import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouteLink } from 'react-router-dom';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Chip } from 'src/components/Chip';
import { Divider } from 'src/components/Divider';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Link } from 'src/components/Link';
import { ListItem } from 'src/components/ListItem';
import { Typography } from 'src/components/Typography';
import { useOptionalSession, useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { FeatureFlag } from 'src/models/platform/_types';
import { signOutUser } from 'src/models/user/user';
import { getUserDisplayName, getUserInitials } from 'src/models/user/utils';
import { PermissionCheck } from 'src/shared/PermissionCheck';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';
import toast from 'src/utils/toast';

interface UserDropdownProps {
  showV2Link?: boolean;
}

export const UserDropdown: React.FC<UserDropdownProps> = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'common.labels' });
  const { t: tMessages } = useTranslation('translation', {
    keyPrefix: 'common.messages',
  });
  const { darkmodeDisabled, isDarkmode, setDarkmode, user, session } = useSession();
  const { refreshSessionAndUser } = useOptionalSession();

  const [open, setOpen] = useState(false);

  const onClickSetDarkMode = () => {
    setDarkmode(true);
  };
  const setLightMode = () => {
    setDarkmode(false);
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
      await refreshSessionAndUser();
      toast.success(tMessages('You have been successfully signed out.'));
      window.location.href = externalLinks.login;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error signing out:', error);
      toast.error(
        tMessages('An error occurred while signing out. Please try again.')
      );
      // Optionally, you can show an error message to the user here
    }
  };

  const useDarkMode = isDarkmode();

  return (
    <DropdownMenu
      open={open}
      setOpen={setOpen}
      drawerOnMobile
      trigger={
        <IconButton onClick={() => setOpen(!open)}>
          <Avatar src={user.profileImage} size="small">
            {getUserInitials(user)}
          </Avatar>
        </IconButton>
      }
    >
      <ListItem
        primary={getUserDisplayName(user)}
        secondary={user.email}
        avatar={
          <Avatar src={user.profileImage} size="small">
            {getUserInitials(user)}
          </Avatar>
        }
        divider
      />
      {!darkmodeDisabled && (
        <Box>
          <Box px={2} pt={1} pb={2}>
            <Typography variant="overline">{t('Color Scheme')}</Typography>
            <Box pt={1}>
              <Chip
                onClick={onClickSetDarkMode}
                icon={<Icon icon={icon({ name: 'moon', style: 'regular' })} />}
                label={t('Dark')}
                active={useDarkMode}
                sx={{ pl: 1, mr: 1 }}
              />
              <Chip
                onClick={setLightMode}
                icon={
                  <Icon icon={icon({ name: 'sun-bright', style: 'regular' })} />
                }
                label={t('Light')}
                active={!useDarkMode}
                sx={{ pl: 1, mr: 1 }}
              />
            </Box>
          </Box>
          <Divider />
        </Box>
      )}

      <Box px={1}>
        <PermissionCheck can="viewStaffUserMenu">
          <ListItem
            component={Link}
            href={hasFeatureFlag(session, FeatureFlag.NewDashboard) ? '/ex4/dashboard' : externalLinks.trainerDashboard}
            sx={{ my: 1 }}
            icon={icon({ name: 'archive', style: 'regular' })}
            primary={t('Staff Member Dashboard')}
            clickable
            rounded
          />
        </PermissionCheck>
        <ListItem
          component={RouteLink}
          to={'/me/notifications'}
          icon={icon({ name: 'dashboard', style: 'regular' })}
          primary={t('Notification Settings')}
          sx={{ my: 1 }}
          clickable
          rounded
        />
        <PermissionCheck can="viewBillingInfo">
          <ListItem
            component={RouteLink}
            to={'/me/account'}
            icon={icon({ name: 'credit-card-blank', style: 'regular' })}
            primary={t('Billing Information')}
            sx={{ my: 1 }}
            clickable
            rounded
          />
        </PermissionCheck>
        <PermissionCheck can="viewStaffUserMenu">
          <ListItem
            component={Link}
            href={externalLinks.support}
            onClick={() => setOpen(false)}
            icon={icon({ name: 'question-circle', style: 'regular' })}
            primary={t('Support')}
            sx={{ my: 1 }}
            clickable
            rounded
          />
        </PermissionCheck>
        <ListItem
          component={Link}
          href={externalLinks.home}
          icon={icon({ name: 'home', style: 'regular' })}
          primary={t('Home')}
          sx={{ my: 1 }}
          clickable
          rounded
        />
      </Box>
      <Divider />
      <Box p={1}>
        <Button size="small" component={Link} onClick={handleSignOut} fullWidth>
          {t('Sign Out')}
        </Button>
      </Box>
    </DropdownMenu>
  );
};
